import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { Toaster, toast } from 'sonner';
import { useEffect, useState } from 'react';
import { initTelegram, haptic } from '@/lib/telegram';
import { Megaphone, Lock, Check, WifiOff } from 'lucide-react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, persister } from '@/lib/query-client';
import { APP_VERSION } from '@/lib/version';
import { useKeyboardOpen } from '@/lib/use-keyboard-open';
import { AuthError, ApiError, TelegramUnavailableError } from '@/lib/api';
import { ErrorReportSheet } from '@/components/ErrorReportSheet';
import { AccessGuard } from '@/components/AccessGuard';
import { TabBar } from '@/components/TabBar';
import { autoLog } from '@/lib/error-report';
import { useScreenTracking } from '@/lib/screen-track';

/** Верх стопки тостов: под вырезом экрана и шапкой Telegram, как у страницы. */
const TOAST_TOP =
  'calc(max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 8px)';

export const Route = createRootRoute({ component: RootComponent });

// Анти-спам: один 5xx-toast не чаще раза в 5с (refetch'и лупят ошибку).
// Тост висит бесконечно (duration: Infinity) с кнопкой «Сообщить» — время нажать.
// openSheet прокидывается из компонента (доступ к state).
let lastServerErrorToast = 0;
function notifyServerError(error: unknown, openSheet: (e: unknown) => void) {
  const now = Date.now();
  if (now - lastServerErrorToast < 5000) return;
  lastServerErrorToast = now;
  toast.error('Сервер недоступен, попробуйте позже', {
    description: 'Данные временно не загружаются. Повторим автоматически.',
    duration: Infinity,
    action: {
      label: 'Сообщить',
      onClick: () => openSheet(error),
    },
  });
}

function RootComponent() {
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const keyboardOpen = useKeyboardOpen();
  useScreenTracking();
  const [authError, setAuthError] = useState<AuthError | null>(null);
  // Telegram не отдал данные для входа (SDK не загрузился / пустая initData).
  const [tgError, setTgError] = useState<TelegramUnavailableError | null>(null);
  // Ошибка, по которой открыта форма отчёта (из persistent-тоста). null = закрыта.
  const [reportSheetError, setReportSheetError] = useState<unknown>(null);

  useEffect(() => {
    initTelegram();
    // Глобальный перехватчик auth-ошибок — JWT истёк / партнёр забанен. Не показываем
    // mock, показываем явный экран повторной авторизации. До этого fix'а partner-api.ts
    // глушил 401/403 пустым fallback'ом → забаненный партнёр видел «рабочий» пустой кабинет.
    const handleError = (error: unknown) => {
      // Данные от Telegram не пришли — запрос до бэка не дошёл, в серверных логах такой
      // случай не виден вообще. Шлём маячок (эндпоинт принимает и без токена), иначе мы
      // о таких отказах узнаём только когда партнёр напишет в поддержку.
      if (error instanceof TelegramUnavailableError) {
        setTgError(error);
        void autoLog(error, { severity: 'error' });
        return;
      }
      if (error instanceof AuthError) {
        setAuthError(error);
        return;
      }
      // 5xx / сетевой сбой (status 0) — бэк лёг. Тихо логируем на бэк (авто-лог с
      // дедупом) + persistent-toast с кнопкой «Сообщить».
      if (error instanceof ApiError && (error.status >= 500 || error.status === 0)) {
        void autoLog(error, { severity: 'error' });
        notifyServerError(error, setReportSheetError);
      }
    };
    // Просканировать уже осевшие в кэше ошибки: prefetch'и из main.tsx стартуют ДО
    // mount'а root, их ошибка приходит раньше подписки ниже.
    for (const q of queryClient.getQueryCache().getAll()) {
      if (q.state.status === 'error') handleError(q.state.error);
    }

    const unsubQ = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        handleError(event.action.error);
      }
    });
    const unsubM = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action?.type === 'error') {
        handleError(event.action.error);
      }
    });
    return () => {
      unsubQ();
      unsubM();
    };
  }, []);

  // Telegram не отдал данные для входа. Отдельный экран от «Доступ запрещён»: тут нет
  // проблемы с аккаунтом, и совет «перезапустите WebApp» бесполезен.
  if (tgError) {
    const noSdk = tgError.reason === 'no_sdk';
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background text-foreground space-y-4">
        <div className="w-16 h-16 bg-amber-500/15 text-amber-500 rounded-full flex items-center justify-center">
          <WifiOff className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold">{noSdk ? 'Telegram не отвечает' : 'Откройте кабинет из бота'}</h2>
        <p className="text-muted-foreground max-w-sm">
          {noSdk
            ? 'Не удалось получить данные для входа. Обычно это обрыв связи или включённый VPN. Проверьте интернет и откройте кабинет заново.'
            : 'Данные для входа передаёт Telegram. По прямой ссылке в браузере кабинет не работает: откройте его кнопкой меню в боте.'}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-primary text-primary-foreground px-6 py-3 rounded-xl text-sm font-bold"
        >
          Обновить
        </button>
      </div>
    );
  }

  // Гейт обязательной подписки: бэк вернул 403 app.subscription_required.
  if (authError && authError.code === 'app.subscription_required') {
    const retry = () => {
      haptic('success');
      queryClient.clear();
      setAuthError(null);
    };
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background text-foreground space-y-4">
        <div className="w-16 h-16 bg-white/[0.07] text-foreground rounded-full flex items-center justify-center">
          <Megaphone className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold">Подпишись на канал</h2>
        <p className="text-muted-foreground max-w-sm">
          Чтобы пользоваться кабинетом, подпишись на наш канал партнёров. Там обновления, фишки и анонсы.
        </p>
        {authError.channelUrl && (
          <a
            href={authError.channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => haptic('light')}
            className="w-full max-w-sm bg-primary text-primary-foreground px-6 py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2"
          >
            <Megaphone className="w-4 h-4" /> Подписаться на канал
          </a>
        )}
        <button
          type="button"
          onClick={retry}
          className="w-full max-w-sm bg-white/10 text-foreground px-6 py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" /> Я подписался
        </button>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background text-foreground space-y-4">
        <div className="w-16 h-16 bg-red-500/15 text-red-500 rounded-full flex items-center justify-center">
          <Lock className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold">Доступ запрещён</h2>
        <p className="text-muted-foreground max-w-sm">
          {authError.status === 403
            ? 'Аккаунт партнёра деактивирован или нет доступа. Обратитесь в поддержку.'
            : 'Сессия истекла. Перезапусти WebApp из бота.'}
        </p>
        <button
          type="button"
          onClick={() => {
            queryClient.clear();
            setAuthError(null);
          }}
          className="bg-primary text-primary-foreground px-6 py-3 rounded-xl text-sm font-bold"
        >
          Повторить
        </button>
      </div>
    );
  }

  const showNav = !currentPath.startsWith('/onboarding') && currentPath !== '/';

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: persister!, maxAge: 24 * 60 * 60 * 1000, buster: APP_VERSION }}
    >
      <div
        className="min-h-screen bg-background text-foreground"
        style={{
          // Вверху страницы два разных отступа, и складывать их напрямую нельзя.
          // `env(safe-area-inset-top)` это вырез экрана, но в вебвью Telegram он
          // на части устройств приходит нулём — там то же значение отдаётся
          // переменной `--tg-safe-area-inset-top`. Это ОДНО И ТО ЖЕ, поэтому
          // берём max, а не сумму: сумма даёт двойной отступ на айфоне.
          // `--tg-content-safe-area-inset-top` — своя шапка Telegram («✕ Закрыть»,
          // ⌄, ⋮), она рисуется ПОВЕРХ контента в fullscreen, и вот её нужно
          // прибавлять. Без неё верх страницы уезжает под шапку (заявка #56).
          paddingTop:
            'calc(max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 8px)',
          // Внизу тот же принцип: навигация стоит на `env(safe-area-inset-bottom) + 12px`,
          // а запас под контентом был числом без учёта этого отступа. На устройствах с
          // большим системным отступом (жестовая навигация) пилюля поднималась выше и
          // накрывала последний ряд контента (заявка #69).
          paddingBottom: showNav ? 'calc(env(safe-area-inset-bottom) + 110px)' : undefined,
        }}
      >
        <div className="w-full max-w-md mx-auto">
          <Outlet />
        </div>

        <AccessGuard pathname={currentPath} />
        {showNav && <TabBar pathname={currentPath} hidden={keyboardOpen} />}

        {/* Тосты сверху. Снизу они ложились на кнопки шторок, а вечный тост об ошибке
            сервера закрывал их, пока его не смахнут. Отступ снизу к тому же не
            работал на телефоне: sonner ниже 600px берёт `mobileOffset`. */}
        <Toaster
          position="top-center"
          /* Кабинет всегда тёмный (см. index.css), а "system" отдавал белые тосты
             тем, у кого светлая тема ОС — на скриншоте это било в глаза. */
          theme="dark"
          richColors
          offset={{ top: TOAST_TOP }}
          mobileOffset={{ top: TOAST_TOP }}
        />

        <ErrorReportSheet
          error={reportSheetError}
          open={reportSheetError !== null}
          onClose={() => setReportSheetError(null)}
        />
      </div>
    </PersistQueryClientProvider>
  );
}


